import React from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

export const Solutions = () => {
    const [solutionsRef, isSolutionsVisible] = useScrollAnimation({ threshold: 0.2 });
    const [gridRef, isGridVisible] = useScrollAnimation({ threshold: 0.15 });
    const [calculatorRef, isCalculatorVisible] = useScrollAnimation({ threshold: 0.3 });

    // Data for the cards
    const cardsData = [
        {
            title: "Fleet Owners",
            description: "Empowering your fleet with cost-effective solutions for a seamless transition to electric trucks at one click.",
        },
        {
            title: "Charging Station Developers",
            description: "Strategic guide to optimal locations with data-driven insights and growth potential.",
        },
        {
            title: "Utilities",
            description: "Plan for fleet-scale charging, optimize grid capacity, and leverage V2G for efficiency.",
        }
    ];

    return (
        <div className="p-4 sm:px-6 lg:px-8 py-8 my-12 space-y-16">
            {/* Main Solutions Card */}
            <div 
                ref={solutionsRef}
                id="solutions" 
                className={`bg-white rounded-[20px] p-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
                    isSolutionsVisible 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12">
                    <div className="flex-1 space-y-6">
                        <h1 className="text-4xl lg:text-5xl font-nunito font-bold text-black leading-tight">
                            Our solutions
                        </h1>
                        <p className="text-lg text-gray-500 leading-relaxed font-nunito">
                            We foster a deep understanding of the electric ecosystem through advanced modeling, AI, and data analysis - key to coordinating development effectively and efficiently.
                        </p>
                        <p className="text-lg text-gray-500 leading-relaxed font-nunito">
                            Fleet electrification is a complex process that requires a deep understanding of the electric ecosystem. We leverage advanced modeling, AI, and data analysis to coordinate development effectively and efficiently.
                        </p>
                        <div className="pt-4">
                            <Link 
                                to="/subscription" 
                                className="btn-primary btn-lg"
                            >
                                Get started
                            </Link>
                        </div>
                    </div>
                    <div className="flex-1">
                        <img 
                            className="w-full h-auto rounded-lg shadow-md" 
                            src='img/homepage/charging-infrastructure-homepage.png' 
                            alt="trucks" 
                        />
                    </div>
                </div>
            </div>

            {/* Solutions Grid Card */}
            <div 
                ref={gridRef}
                className={`bg-white rounded-[20px] p-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
                    isGridVisible 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h1 className="text-3xl lg:text-4xl font-nunito font-bold text-black mb-6">
                            What can we do for you?
                        </h1>
                    </div>

                    {/* Grid of Cards */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {cardsData.map((card, index) => (
                            <div 
                                key={index} 
                                className={`bg-gray-50 rounded-xl p-8 h-full transition-all duration-1000 ease-out transform ${
                                    isGridVisible 
                                        ? 'opacity-100 translate-y-0' 
                                        : 'opacity-0 translate-y-8'
                                }`}
                                style={{
                                    transitionDelay: isGridVisible ? `${index * 200}ms` : '0ms'
                                }}
                            >
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-nunito font-bold text-black mb-4">
                                        {card.title}
                                    </h3>
                                    <p className="text-base text-gray-600 leading-relaxed font-nunito">
                                        {card.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Calculator Card */}
            <div 
                ref={calculatorRef}
                className={`bg-white rounded-[20px] p-12 lg:p-16 shadow-sm transition-all duration-1000 ease-out transform ${
                    isCalculatorVisible 
                        ? 'opacity-100 translate-y-0' 
                        : 'opacity-0 translate-y-8'
                }`}
            >
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-3xl lg:text-4xl font-nunito font-bold text-black mb-8">
                        Find the tool that best fits your fleet today
                    </h1>
                    <div>
                        <Link 
                            to="/calculator" 
                            className="btn-primary btn-lg"
                        >
                            Explore your options
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    )
}